import { useEffect, useState } from "react";

interface ImageCarouselProps {
  // Flexible image array - can be strings or objects with fileDownloadUri
  images: Array<string | { fileDownloadUri: string }>;
  getImagePath?: (path: string) => string;
  autoPlayInterval?: number;
  height?: string;
  className?: string;
  showIndicators?: boolean;
  imageClassName?: string;
  altPrefix?: string;
}

const ImageCarousel: React.FC<ImageCarouselProps> = ({
  images,
  getImagePath,
  autoPlayInterval = 6000,
  height = "h-[400px]",
  className = "",
  showIndicators = true,
  imageClassName = "",
  altPrefix = "Image",
}) => {
  const [imageIndex, setImageIndex] = useState(0);

  useEffect(() => {
    if (images && images.length > 1) {
      const interval = setInterval(() => {
        setImageIndex((prevIndex) => (prevIndex + 1) % images.length);
      }, autoPlayInterval);

      return () => clearInterval(interval);
    }
  }, [images, autoPlayInterval]);

  if (!images || images.length === 0) {
    return <p className="text-gray-500">No images available</p>;
  }

  const getImageSrc = (image: string | { fileDownloadUri: string }) => {
    if (typeof image === "string") {
      return image;
    }
    return getImagePath ? getImagePath(image.fileDownloadUri) : image.fileDownloadUri;
  };

  return (
    <div className={`relative w-full ${height} overflow-hidden rounded-lg shadow-lg ${className}`}>
      {images.map((pic, index) => (
        <img
          key={index}
          src={getImageSrc(pic)}
          alt={`${altPrefix} ${index + 1}`}
          className={`w-full h-full object-cover absolute transition-opacity duration-3000 ease-in-out ${
            imageIndex === index ? "fadeIn" : "fadeOut"
          } ${imageClassName}`}
        />
      ))}

      {/* Image indicators */}
      {showIndicators && images.length > 1 && (
        <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex gap-2">
          {images.map((_, index) => (
            <button
              key={index}
              onClick={() => setImageIndex(index)}
              className={`w-2 h-2 rounded-full transition-all ${
                imageIndex === index
                  ? "bg-white w-8"
                  : "bg-white/50 hover:bg-white/75"
              }`}
              aria-label={`Go to image ${index + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default ImageCarousel;

